import { useNavigate } from 'react-router-dom';
import { Sparkles, Award, Lock, Trophy } from 'lucide-react';
import { RELIGIONS } from '../utils/godsQuestions';
import { getBadges } from '../utils/godsBadges';

const GODS_BG = 'https://static.prod-images.emergentagent.com/jobs/0e7ec82b-be21-4167-ae09-299c58207254/images/7b221cc89bb32c26551da609b6dc5932d3395b8dd600bcbd9048f845c7c2c510.jpeg';

export default function GodsHomePage() {
  const navigate = useNavigate();
  const badges = getBadges();

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center p-6"
      style={{ backgroundImage: `url(${GODS_BG})`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundAttachment: 'fixed' }}
    >
      <div className="bg-white/95 backdrop-blur-md border-4 border-white rounded-3xl shadow-2xl p-8 sm:p-10 max-w-lg w-full text-center">
        <div className="w-20 h-20 rounded-full bg-gradient-to-br from-amber-400 to-orange-600 flex items-center justify-center mx-auto mb-4 shadow-lg border-4 border-white">
          <Sparkles className="w-10 h-10 text-white" strokeWidth={2.5} />
        </div>

        <h1 className="font-heading text-4xl sm:text-5xl font-black text-gray-900 mb-2">
          Gods &amp; Goddesses
        </h1>
        <p className="font-body text-base text-gray-600 mb-6 leading-relaxed">
          Pick a religion and answer 20 questions about its gods, gurus and festivals!
        </p>

        <p className="font-heading text-lg font-black text-gray-800 mb-4">Choose your religion</p>

        <div className="grid grid-cols-2 gap-4">
          {RELIGIONS.map((r) => (
            <button
              key={r.id}
              data-testid={`gods-religion-${r.id}`}
              onClick={() => navigate(`/gods/play?religion=${r.id}`)}
              className="bg-white rounded-2xl border-2 border-gray-100 p-5 flex flex-col items-center gap-3 shadow-md hover:shadow-xl hover:-translate-y-1 transition-all duration-200"
            >
              <span
                className="w-14 h-14 rounded-full flex items-center justify-center text-white text-3xl leading-none shadow-inner"
                style={{ background: r.gradient }}
                aria-hidden="true"
              >
                {r.symbol}
              </span>
              <span className="font-heading text-base font-black text-gray-900">{r.name}</span>
              {badges.includes(r.id) && (
                <span
                  data-testid={`gods-badge-${r.id}`}
                  className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wide text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full"
                >
                  <Award className="w-3 h-3" />
                  Badge
                </span>
              )}
            </button>
          ))}
        </div>

        <div className="mt-6 rounded-3xl border-2 border-amber-100 bg-amber-50/60 p-4" data-testid="gods-badge-case">
          <p className="font-heading text-sm font-black text-amber-800 mb-3">
            My Badges — {badges.length} of {RELIGIONS.length}
          </p>
          <div className="flex items-center justify-center gap-4">
            {RELIGIONS.map((r) => {
              const won = badges.includes(r.id);
              return (
                <div key={`case-${r.id}`} className="flex flex-col items-center gap-1">
                  <span
                    className={`w-11 h-11 rounded-full flex items-center justify-center text-white text-xl leading-none shadow ${won ? 'ring-2 ring-amber-300' : 'grayscale opacity-40'}`}
                    style={{ background: r.gradient }}
                    aria-hidden="true"
                  >
                    {won ? r.symbol : <Lock className="w-4 h-4" />}
                  </span>
                  <span className="font-body text-[10px] font-bold text-gray-600">{r.name}</span>
                </div>
              );
            })}
          </div>
          <p className="font-body text-xs text-gray-500 mt-3">Score 18 or more out of 20 to win that religion&apos;s badge!</p>
        </div>

        {badges.length === RELIGIONS.length && (
          <div
            data-testid="gods-master-trophy-home"
            className="mt-4 rounded-3xl p-5 shadow-xl flex items-center gap-4 text-left"
            style={{ background: 'linear-gradient(160deg, #78350f 0%, #b45309 45%, #f59e0b 100%)' }}
          >
            <span
              className="w-16 h-16 shrink-0 rounded-full flex items-center justify-center shadow-2xl ring-4 ring-amber-200/70"
              style={{ background: 'radial-gradient(circle at 30% 25%, #fffbeb, #fde047 35%, #f59e0b 75%, #b45309 100%)' }}
            >
              <Trophy className="w-8 h-8 text-amber-900" strokeWidth={2.5} />
            </span>
            <div>
              <p className="font-heading text-lg font-black text-amber-50 leading-tight">MASTER OF ALL FAITHS</p>
              <p className="font-body text-xs font-bold text-amber-100/90">Golden trophy earned — all four badges collected!</p>
            </div>
          </div>
        )}

        <button
          data-testid="gods-back-studio"
          onClick={() => navigate('/')}
          className="mt-6 text-gray-500 font-bold text-sm hover:text-gray-800 transition-colors underline"
        >
          Back to Studio
        </button>
      </div>
    </div>
  );
}
